import { Link } from "@tanstack/solid-router";
import { For, Show, createMemo, createSignal } from "solid-js";

import {
  useAccounts,
  useAddContactAccount,
  useContactAccounts,
  useRemoveContactAccount,
  useUpdateContactAccount,
} from "~/lib/hooks";

type Props = {
  wsKey: () => string;
  contactId: () => string;
};

export function ContactAccountsCard(props: Props) {
  const links = useContactAccounts(props.wsKey, props.contactId);
  const addMutation = useAddContactAccount(props.wsKey, props.contactId);
  const updateMutation = useUpdateContactAccount(props.wsKey, props.contactId);
  const removeMutation = useRemoveContactAccount(props.wsKey, props.contactId);

  const [picking, setPicking] = createSignal(false);
  const [pickerQuery, setPickerQuery] = createSignal("");
  const [pickerErr, setPickerErr] = createSignal<string | null>(null);
  const [roleDraft, setRoleDraft] = createSignal("");
  const [makePrimary, setMakePrimary] = createSignal(false);

  const accounts = useAccounts(props.wsKey, () => ({
    q: pickerQuery() || undefined,
    limit: 20,
  }));

  const alreadyLinkedIds = createMemo(
    () => new Set((links.data?.items ?? []).map((l) => l.account_id)),
  );

  const handleAdd = async (accountId: string) => {
    setPickerErr(null);
    try {
      await addMutation.mutateAsync({
        account_id: accountId,
        is_primary: makePrimary(),
        attributes: roleDraft() ? { role: roleDraft() } : null,
      });
      setPicking(false);
      setPickerQuery("");
      setRoleDraft("");
      setMakePrimary(false);
    } catch (err) {
      setPickerErr((err as Error).message);
    }
  };

  const handlePromote = async (accountId: string) => {
    setPickerErr(null);
    try {
      await updateMutation.mutateAsync({
        accId: accountId,
        body: { is_primary: true },
      });
    } catch (err) {
      setPickerErr((err as Error).message);
    }
  };

  const handleRemove = async (accountId: string) => {
    setPickerErr(null);
    try {
      await removeMutation.mutateAsync({ accId: accountId });
    } catch (err) {
      setPickerErr((err as Error).message);
    }
  };

  return (
    <div class="card bg-base-100 shadow-sm">
      <div class="card-body">
        <div class="flex items-center justify-between">
          <h3 class="card-title text-lg">Accounts</h3>
          <button
            type="button"
            class="btn btn-sm btn-primary"
            onClick={() => {
              setPicking((v) => !v);
              setPickerErr(null);
            }}
          >
            {picking() ? "Cancel" : "Add account"}
          </button>
        </div>

        <Show when={pickerErr()}>
          <div role="alert" class="alert alert-error text-sm mt-2">
            {pickerErr()}
          </div>
        </Show>

        <Show when={picking()}>
          <div class="border rounded-lg p-3 mt-2 bg-base-200">
            <div class="grid gap-2 sm:grid-cols-3">
              <input
                class="input input-bordered input-sm sm:col-span-2"
                placeholder="Search accounts…"
                aria-label="Search accounts"
                value={pickerQuery()}
                onInput={(e) => setPickerQuery(e.currentTarget.value)}
              />
              <input
                class="input input-bordered input-sm"
                placeholder="Role (optional)"
                aria-label="Role"
                value={roleDraft()}
                onInput={(e) => setRoleDraft(e.currentTarget.value)}
              />
            </div>
            <label class="label cursor-pointer w-fit mt-2">
              <input
                type="checkbox"
                class="checkbox checkbox-sm mr-2"
                checked={makePrimary()}
                onChange={(e) => setMakePrimary(e.currentTarget.checked)}
              />
              <span class="label-text">Make this the primary account</span>
            </label>
            <ul class="mt-2 max-h-56 overflow-y-auto divide-y divide-base-300">
              <For each={accounts.data?.items ?? []}>
                {(a) => {
                  const linked = alreadyLinkedIds().has(a.id);
                  return (
                    <li class="py-2 flex items-center justify-between gap-2">
                      <div class="min-w-0">
                        <div class="font-medium truncate">{a.name}</div>
                        <div class="text-xs opacity-60 truncate">
                          {a.primary_domain ?? "—"}
                        </div>
                      </div>
                      <button
                        type="button"
                        class="btn btn-xs"
                        disabled={linked || addMutation.isPending}
                        onClick={() => handleAdd(a.id)}
                      >
                        {linked ? "Linked" : "Add"}
                      </button>
                    </li>
                  );
                }}
              </For>
              <Show when={(accounts.data?.items.length ?? 0) === 0}>
                <li class="py-3 text-center opacity-60 text-sm">
                  No accounts match.
                </li>
              </Show>
            </ul>
          </div>
        </Show>

        <Show when={links.isLoading}>
          <div role="status" class="py-4 text-center opacity-60 text-sm">
            Loading…
          </div>
        </Show>
        <Show when={links.isError}>
          <div role="alert" class="alert alert-error text-sm">
            Failed to load accounts.
          </div>
        </Show>

        <Show when={links.data}>
          {(data) => (
            <Show
              when={data().items.length > 0}
              fallback={
                <div class="text-center py-6 opacity-60 text-sm">
                  No linked accounts.
                </div>
              }
            >
              <ul class="divide-y divide-base-300 mt-2">
                <For each={data().items}>
                  {(link) => {
                    const role = (link.attributes?.["role"] as string | undefined) ?? null;
                    return (
                      <li class="py-2 flex items-center justify-between gap-2">
                        <div class="min-w-0">
                          <Link
                            to="/workspaces/$wsKey/accounts/$accId"
                            params={{ wsKey: props.wsKey(), accId: link.account_id }}
                            class="link link-primary font-medium"
                          >
                            {link.account_name}
                          </Link>
                          <Show when={link.is_primary}>
                            <span class="badge badge-primary badge-xs ml-2 align-middle">
                              primary
                            </span>
                          </Show>
                          <div class="text-xs opacity-60 truncate">
                            {[link.account_primary_domain, role]
                              .filter(Boolean)
                              .join(" · ") || "—"}
                          </div>
                        </div>
                        <div class="flex gap-1">
                          <Show when={!link.is_primary}>
                            <button
                              type="button"
                              class="btn btn-xs"
                              disabled={updateMutation.isPending}
                              onClick={() => handlePromote(link.account_id)}
                              title="Make this the primary account"
                            >
                              Promote
                            </button>
                            <button
                              type="button"
                              class="btn btn-xs btn-ghost text-error"
                              disabled={removeMutation.isPending}
                              onClick={() => handleRemove(link.account_id)}
                              aria-label={`Remove ${link.account_name}`}
                            >
                              Remove
                            </button>
                          </Show>
                        </div>
                      </li>
                    );
                  }}
                </For>
              </ul>
            </Show>
          )}
        </Show>
      </div>
    </div>
  );
}
